import qs from 'qs'
import { pathOr } from 'ramda'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom-v5-compat'

import Loading from '@/app/component/guard/loading'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { ERROR_GET_CAMPAIGNS_SERVER } from '@/app/module/campaigns/definitions'
import { useAbortCampaign } from '@/app/module/campaigns/hooks/use-abort-campaign'
import { useCopyCampaign } from '@/app/module/campaigns/hooks/use-copy-campaign'
import { useExportCampaignReport } from '@/app/module/campaigns/hooks/use-export-campaign-report'
import { useGetCampaignsList } from '@/app/module/campaigns/hooks/use-get-campaigns-list'
import { useSetCampaignArchived } from '@/app/module/campaigns/hooks/use-set-campaign-archived'
import { useSetCampaignOpen } from '@/app/module/campaigns/hooks/use-set-campaign-open'
import { useSetCampaignPublished } from '@/app/module/campaigns/hooks/use-set-campaign-published'
import { useUnsubscribeAllContacts } from '@/app/module/campaigns/hooks/use-unsubscribe-all-contacts'
import { CampaignQuery, TCampaignListItem } from '@/app/module/campaigns/types'
import { useOrgSwitchGuard } from '@/app/module/campaigns/utils/campaign'
import { getErrorMessage } from '@/app/store/api'
import {
  CountryTimezone,
  LoadingError,
  PersonalizationType,
  SelectOption,
  TableHeader,
  TableSpecs,
  TimezoneWithCountryCodeType,
} from '@/app/types'
import config from '@/config'

import { getAbortConfirmation, getUnsubAllConfirmation } from './confirmations'
import DownloadReportDialog from './list/download-report'
import CampaignsTable from './list/table'

const defaultPageSize = config.modules.campaigns.pageSize.default

const getErrorStatus = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return undefined
  }

  const status = (error as { status?: number }).status
  return typeof status === 'number' ? status : undefined
}

type Props = {
  countriesTimezones: CountryTimezone[]
  error?: LoadingError
  headers: TableHeader[]
  loading: boolean
  orgId: number
  pageSizeValues?: SelectOption<number>[]
  personalizationList: PersonalizationType
  specs: TableSpecs
  timezone: string
  timezones: TimezoneWithCountryCodeType
  token: string

  createNotification: (data: Record<string, { type: string; message: string }>) => void
  getCustomFields: (payload: { token: string; orgId: number }) => void
}

type State = {
  downloadCampaignReportId: number | null
}

const Campaigns: React.FC<Props> = (props) => {
  const createConfirmation = useConfirmation()
  const navigate = useNavigate()
  const {
    countriesTimezones,
    error,
    loading,
    orgId,
    personalizationList,
    timezone,
    timezones,
    token,
    getCustomFields,
  } = props

  const location = useLocation()
  const query = React.useMemo<CampaignQuery>(() => {
    const q = qs.parse(location.search, { ignoreQueryPrefix: true }) as Partial<Record<keyof CampaignQuery, string>>
    return {
      ...q,
      page: q.page ? Number(q.page) : 1,
      size: q.size ? Number(q.size) : defaultPageSize,
    }
  }, [location.search])

  const {
    data,
    error: campaignsError,
    ordered,
    total,
    isFetching: isCampaignsFetching,
    isLoading: isCampaignsLoading,
    refetch,
  } = useGetCampaignsList(query)
  const [abortCampaign] = useAbortCampaign()
  const [copyCampaign] = useCopyCampaign()
  const [exportCampaignReport] = useExportCampaignReport()
  const [setCampaignArchived] = useSetCampaignArchived()
  const [setCampaignOpen] = useSetCampaignOpen()
  const [setCampaignPublished] = useSetCampaignPublished()
  const [unsubscribeAllContacts] = useUnsubscribeAllContacts()

  const [state, updateState] = React.useState<State>({
    downloadCampaignReportId: null,
  })
  const [localLoadingItems, setLocalLoadingItems] = React.useState<number[]>([])

  useOrgSwitchGuard(orgId)

  React.useEffect(() => {
    if (orgId && token) {
      getCustomFields({ token, orgId })
    }
  }, [token, orgId, getCustomFields])

  const setState = React.useCallback(
    (payload: Partial<State>) =>
      updateState((s) => ({
        ...s,
        ...payload,
      })),
    [],
  )

  const toggleDownloadCampaignReport = (id: number) => {
    setState({
      downloadCampaignReportId: id,
    })
  }

  const setAbort = React.useCallback(
    async (id: number) => {
      setLocalLoadingItems((items) => (items.includes(id) ? items : [...items, id]))
      try {
        await abortCampaign(id)
      } finally {
        setLocalLoadingItems((items) => items.filter((itemId) => itemId !== id))
      }
    },
    [abortCampaign],
  )

  const handleAbortConfirm = React.useCallback(
    async (value: TCampaignListItem) => {
      const campaignName = pathOr('', ['name'], value)
      const campaignId = pathOr(0, ['id'], value)
      if (!campaignId) {
        return
      }

      const confirmed = await createConfirmation(getAbortConfirmation(campaignName))

      if (confirmed) {
        setAbort(campaignId)
      }
    },
    [createConfirmation, setAbort],
  )

  const handleStopAllClick = React.useCallback(
    async (itemId: number) => {
      const toBeStopped = data[itemId]
      if (!toBeStopped) {
        return
      }

      const confirmed = await createConfirmation(getUnsubAllConfirmation(toBeStopped.name))

      if (confirmed) {
        setLocalLoadingItems((items) => (items.includes(itemId) ? items : [...items, itemId]))
        try {
          await unsubscribeAllContacts(itemId)
        } finally {
          setLocalLoadingItems((items) => items.filter((id) => id !== itemId))
        }
      }
    },
    [data, createConfirmation, unsubscribeAllContacts],
  )

  const setPublish = React.useCallback(
    async (id: number, value: boolean, isEdit: boolean) => {
      setLocalLoadingItems((items) => (items.includes(id) ? items : [...items, id]))
      try {
        const response = await setCampaignPublished({
          campaignId: id,
          published: value,
        })

        if (isEdit && response) {
          navigate(`/campaigns/edit/${response.id}`)
        }
      } finally {
        setLocalLoadingItems((items) => items.filter((itemId) => itemId !== id))
      }
    },
    [navigate, setCampaignPublished],
  )

  const setArchived = React.useCallback(
    async (id: number, value: boolean) => {
      setLocalLoadingItems((items) => (items.includes(id) ? items : [...items, id]))
      try {
        await setCampaignArchived({
          campaignId: id,
          archived: value,
        })
      } finally {
        setLocalLoadingItems((items) => items.filter((itemId) => itemId !== id))
      }
    },
    [setCampaignArchived],
  )

  const setOpen = React.useCallback(
    async (id: number, value: boolean) => {
      setLocalLoadingItems((items) => (items.includes(id) ? items : [...items, id]))
      try {
        await setCampaignOpen({
          campaignId: id,
          open: value,
        })
      } finally {
        setLocalLoadingItems((items) => items.filter((itemId) => itemId !== id))
      }
    },
    [setCampaignOpen],
  )

  const copy = React.useCallback(
    async (id: number) => {
      setLocalLoadingItems((items) => (items.includes(id) ? items : [...items, id]))
      try {
        const response = await copyCampaign(id)
        if (response) {
          navigate(`/campaigns/edit/${response.id}/content`)
        }
      } finally {
        setLocalLoadingItems((items) => items.filter((itemId) => itemId !== id))
      }
    },
    [copyCampaign, navigate],
  )

  const downloadReportCampaign = state.downloadCampaignReportId ? data[state.downloadCampaignReportId] : undefined
  const campaignErrorMessage = campaignsError ? getErrorMessage(campaignsError, ERROR_GET_CAMPAIGNS_SERVER) : undefined
  const campaignLoadingError: LoadingError | undefined = campaignErrorMessage
    ? {
        status: getErrorStatus(campaignsError),
        message: campaignErrorMessage,
      }
    : undefined
  const isCampaignsLoadingState = isCampaignsFetching || isCampaignsLoading

  return (
    <Loading isLoading={loading || isCampaignsLoadingState} error={campaignLoadingError || error}>
      {downloadReportCampaign && (
        <DownloadReportDialog
          campaignName={downloadReportCampaign.name}
          countriesTimezones={countriesTimezones}
          isOpen
          personalizationList={personalizationList}
          timezone={timezone}
          timezones={timezones}
          onClose={() => {
            setState({
              downloadCampaignReportId: null,
            })
          }}
          onConfirm={({ timezone: tz, fields, labels }) => {
            setState({
              downloadCampaignReportId: null,
            })
            exportCampaignReport({
              campaignId: state.downloadCampaignReportId as number,
              fields,
              labels,
              timezone: tz,
            })
          }}
        />
      )}
      <CampaignsTable
        {...props}
        data={data}
        loading={loading || isCampaignsLoadingState}
        loadingItems={localLoadingItems}
        ordered={ordered}
        page={query.page}
        query={query}
        root="campaigns"
        total={total}
        onArchive={(id: number) => setArchived(id, true)}
        onClose={(id: number) => setOpen(id, false)}
        onCopy={(id: number) => copy(id)}
        onOpen={(id: number) => setOpen(id, true)}
        onPublish={(id: number) => setPublish(id, true, false)}
        onRefresh={refetch}
        onStopAllClick={handleStopAllClick}
        onUnpublish={(id: number) => setPublish(id, false, false)}
        onUnpublishThenEdit={(id: number) => setPublish(id, false, true)}
        onUnarchive={(id: number) => setArchived(id, false)}
        toggleAbortConfirm={(item: TCampaignListItem) => handleAbortConfirm(item)}
        toggleDownloadCampaignReport={(id: number) => toggleDownloadCampaignReport(id)}
      />
    </Loading>
  )
}

export default Campaigns
